// src/services/providerAccountService.js - 송금업체 계좌 관리 서비스
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig.jsx';

const COLLECTION_NAME = 'providerAccounts';

class ProviderAccountService {
  constructor() {
    this.appId = import.meta.env.VITE_APP_ID || 'kairos-platform-1ffde';
  }

  // 모든 송금업체 계좌 조회
  async getAllAccounts() {
    try {
      console.log('🔍 송금업체 계좌 조회 시작');
      const accountsRef = collection(db, `artifacts/${this.appId}/${COLLECTION_NAME}`);
      const q = query(accountsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const accounts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('✅ 송금업체 계좌 조회 완료:', accounts.length, '개');
      return accounts;
    } catch (error) {
      console.error('❌ 송금업체 계좌 조회 실패:', error);
      throw error;
    }
  }

  // 활성 계좌만 조회 (Frontend용)
  async getActiveAccounts() {
    try {
      console.log('🔍 활성 송금업체 계좌 조회 시작');
      const accounts = await this.getAllAccounts();
      const activeAccounts = accounts.filter(account => account.isActive);
      
      console.log('✅ 활성 송금업체 계좌 조회 완료:', activeAccounts.length, '개');
      return activeAccounts;
    } catch (error) {
      console.error('❌ 활성 송금업체 계좌 조회 실패:', error);
      throw error;
    }
  }

  // 계좌 생성
  async createAccount(accountData) {
    try {
      console.log('📝 송금업체 계좌 생성 시작:', accountData);
      
      const accountRef = collection(db, `artifacts/${this.appId}/${COLLECTION_NAME}`);
      const newAccount = {
        ...accountData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usageCount: 0,
        totalAmount: 0
      };
      
      const docRef = await addDoc(accountRef, newAccount);
      console.log('✅ 송금업체 계좌 생성 완료:', docRef.id);
      
      return {
        id: docRef.id,
        ...newAccount
      };
    } catch (error) {
      console.error('❌ 송금업체 계좌 생성 실패:', error);
      throw error;
    }
  }

  // 계좌 수정
  async updateAccount(accountId, accountData) {
    try {
      console.log('📝 송금업체 계좌 수정 시작:', accountId, accountData);
      
      const accountRef = doc(db, `artifacts/${this.appId}/${COLLECTION_NAME}`, accountId);
      const updateData = {
        ...accountData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(accountRef, updateData);
      console.log('✅ 송금업체 계좌 수정 완료:', accountId);
      
      return { success: true };
    } catch (error) {
      console.error('❌ 송금업체 계좌 수정 실패:', error);
      throw error;
    }
  }

  // 계좌 삭제
  async deleteAccount(accountId) {
    try {
      console.log('🗑️ 송금업체 계좌 삭제 시작:', accountId);
      
      const accountRef = doc(db, `artifacts/${this.appId}/${COLLECTION_NAME}`, accountId);
      await deleteDoc(accountRef);
      
      console.log('✅ 송금업체 계좌 삭제 완료:', accountId);
      return { success: true };
    } catch (error) {
      console.error('❌ 송금업체 계좌 삭제 실패:', error);
      throw error;
    }
  }

  // 계좌 상태 토글
  async toggleAccountStatus(accountId, isActive) {
    try {
      console.log('🔄 송금업체 계좌 상태 변경 시작:', accountId, isActive);
      
      const accountRef = doc(db, `artifacts/${this.appId}/${COLLECTION_NAME}`, accountId);
      await updateDoc(accountRef, {
        isActive,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ 송금업체 계좌 상태 변경 완료:', accountId);
      return { success: true };
    } catch (error) {
      console.error('❌ 송금업체 계좌 상태 변경 실패:', error);
      throw error;
    }
  }

  // 계좌 사용 통계 업데이트 (송금 완료 시 호출)
  async updateAccountUsage(accountId, amount) {
    try {
      console.log('📊 송금업체 계좌 사용 통계 업데이트:', accountId, amount);
      
      const accountRef = doc(db, `artifacts/${this.appId}/${COLLECTION_NAME}`, accountId);
      const accountDoc = await getDoc(accountRef);
      
      if (accountDoc.exists()) {
        const currentData = accountDoc.data();
        await updateDoc(accountRef, {
          usageCount: (currentData.usageCount || 0) + 1,
          totalAmount: (currentData.totalAmount || 0) + amount,
          updatedAt: serverTimestamp()
        });
        
        console.log('✅ 송금업체 계좌 사용 통계 업데이트 완료:', accountId);
      }
    } catch (error) {
      console.error('❌ 송금업체 계좌 사용 통계 업데이트 실패:', error);
      throw error;
    }
  }

  // 계좌 검증 (한도 체크)
  async validateAccountLimits(accountId, amount) {
    try {
      console.log('🔍 송금업체 계좌 한도 검증:', accountId, amount);
      
      const accounts = await this.getAllAccounts();
      const account = accounts.find(acc => acc.id === accountId);
      
      if (!account) {
        throw new Error('계좌를 찾을 수 없습니다.');
      }
      
      if (!account.isActive) {
        throw new Error('비활성 상태의 계좌입니다.');
      }
      
      // 일일 한도 체크 (실제로는 더 복잡한 로직 필요)
      if (account.dailyLimit > 0 && amount > account.dailyLimit) {
        throw new Error(`일일 한도(${account.dailyLimit.toLocaleString()}원)를 초과했습니다.`);
      }
      
      // 월간 한도 체크 (실제로는 더 복잡한 로직 필요)
      if (account.monthlyLimit > 0 && amount > account.monthlyLimit) {
        throw new Error(`월간 한도(${account.monthlyLimit.toLocaleString()}원)를 초과했습니다.`);
      }
      
      console.log('✅ 송금업체 계좌 한도 검증 통과:', accountId);
      return { valid: true, account };
    } catch (error) {
      console.error('❌ 송금업체 계좌 한도 검증 실패:', error);
      throw error;
    }
  }
}

export const providerAccountService = new ProviderAccountService();
export default providerAccountService;

